import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCartStore } from '../../../store/cartStore';
import {
  listMyAddresses,
  createMyAddress,
  CustomerAddress,
} from '../../../services/customerAddressesApi';
import { createOrder } from '../../../services/ordersApi';
import styles from './CustomerCart.module.css';

/**
 * Customer checkout page — mirrors the public ecommerce app's CheckoutPage:
 * full billing form (name/company/country/address/phone/email), saved
 * address picker, order summary, payment method, success overlay.
 *
 * Backend POST /api/v1/orders only needs shipping_address_id + items.
 * The extra form metadata (firstName/lastName/company/phone/email/payment/
 * notes) is captured into sessionStorage for the order confirmation screen
 * so it isn't lost, matching the ecommerce flow.
 */

interface UsState { name: string; code: string }

const US_STATES: UsState[] = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' },
  { name: 'Arizona', code: 'AZ' }, { name: 'Arkansas', code: 'AR' },
  { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' },
  { name: 'Florida', code: 'FL' }, { name: 'Georgia', code: 'GA' },
  { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' },
  { name: 'Iowa', code: 'IA' }, { name: 'Kansas', code: 'KS' },
  { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' },
  { name: 'Massachusetts', code: 'MA' }, { name: 'Michigan', code: 'MI' },
  { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' },
  { name: 'Nebraska', code: 'NE' }, { name: 'Nevada', code: 'NV' },
  { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' },
  { name: 'North Carolina', code: 'NC' }, { name: 'North Dakota', code: 'ND' },
  { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' },
  { name: 'Rhode Island', code: 'RI' }, { name: 'South Carolina', code: 'SC' },
  { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' },
  { name: 'Vermont', code: 'VT' }, { name: 'Virginia', code: 'VA' },
  { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' },
];

const PAYMENT_METHODS = [
  { id: 'bank',  label: 'Direct Bank Transfer', desc: 'Make your payment directly into our bank account. Please use your Order ID as the payment reference.' },
  { id: 'check', label: 'Check Payment',        desc: 'Please send a check to our store address. Your order will be shipped after the check clears.' },
  { id: 'cod',   label: 'Cash on Delivery',     desc: 'Pay with cash upon delivery. A small handling fee may apply.' },
];

const TAX_RATE = 0.08;
const FREE_SHIPPING_THRESHOLD = 50;
const SHIPPING_COST = 9.99;

const initialForm = {
  firstName: '',
  lastName: '',
  company: '',
  country: 'United States (US)',
  street1: '',
  street2: '',
  city: '',
  state: 'WA',          // stored as 2-letter code; dropdown shows full name
  zip: '',
  phone: '',
  email: '',
  createAccount: false,
  orderNotes: '',
};

const CustomerCart: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const items = useCartStore((s) => s.items);
  const setQuantity = useCartStore((s) => s.setQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const clear = useCartStore((s) => s.clear);
  const subtotal = useCartStore((s) => s.subtotal());

  const [form, setForm] = useState({
    ...initialForm,
    email: user?.email ?? '',
    firstName: (user?.full_name ?? '').split(' ')[0] ?? '',
    lastName: (user?.full_name ?? '').split(' ').slice(1).join(' '),
  });

  const [savedAddresses, setSavedAddresses] = useState<CustomerAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [error, setError] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);
  const [placedOrderNum, setPlacedOrderNum] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listMyAddresses()
      .then((rows) => {
        if (cancelled) return;
        const shipping = rows.filter((a) => a.address_type === 'shipping');
        setSavedAddresses(shipping);
        const def = shipping.find((a) => a.is_default) ?? shipping[0];
        if (def) {
          setSelectedAddressId(def.id);
          setForm((prev) => ({
            ...prev,
            street1: def.address_line1,
            street2: def.address_line2 ?? '',
            city: def.city,
            state: def.state,
            zip: def.zip_code,
          }));
        }
      })
      .catch(() => { /* not authed / new account — fine, fall through to manual entry */ });
    return () => { cancelled = true; };
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePickAddress = (id: number | null) => {
    setSelectedAddressId(id);
    if (id === null) {
      setForm((prev) => ({ ...prev, street1: '', street2: '', city: '', zip: '' }));
      return;
    }
    const addr = savedAddresses.find((a) => a.id === id);
    if (!addr) return;
    setForm((prev) => ({
      ...prev,
      street1: addr.address_line1,
      street2: addr.address_line2 ?? '',
      city: addr.city,
      state: addr.state,
      zip: addr.zip_code,
    }));
  };

  const isFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = isFreeShipping ? 0 : SHIPPING_COST;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + shipping + tax;

  const isFormValid = useMemo(() => {
    return Boolean(
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.street1.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.zip.trim() &&
      form.phone.trim() &&
      form.email.trim()
    );
  }, [form]);

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || items.length === 0) return;
    setError(null);
    setPlacing(true);
    try {
      // 1) Resolve shipping_address_id. If a saved address is picked, reuse;
      //    otherwise create a new one from the form (auto-default if first).
      let shippingAddressId = selectedAddressId;
      if (!shippingAddressId) {
        const created = await createMyAddress({
          address_type: 'shipping',
          address_line1: form.street1.trim(),
          address_line2: form.street2.trim() || undefined,
          city: form.city.trim(),
          state: form.state.toUpperCase(),
          zip_code: form.zip.trim(),
          is_default: savedAddresses.length === 0,
        });
        shippingAddressId = created.id;
        setSavedAddresses((prev) => [created, ...prev]);
        setSelectedAddressId(created.id);
      }

      // 2) Create the order. Backend only needs shipping_address_id + items.
      const order = await createOrder({
        shipping_address_id: shippingAddressId,
        items: items.map((l) => ({
          sku_id: l.sku_id,
          quantity: l.quantity,
          unit_price: l.unit_price,
        })),
      });

      // 3) Persist the full checkout context (the extra fields the backend
      //    doesn't store) so an order-confirmation screen can render them.
      try {
        sessionStorage.setItem(
          'lastOrder',
          JSON.stringify({
            orderNumber: `VIN-${order.id}`,
            items: items.map((l) => ({ name: l.name, qty: l.quantity, price: l.unit_price, image: l.image_url ?? null })),
            subtotal, shipping, tax, total,
            paymentMethod: PAYMENT_METHODS.find((p) => p.id === paymentMethod)?.label ?? paymentMethod,
            address: {
              name: `${form.firstName} ${form.lastName}`.trim(),
              company: form.company,
              street: form.street1,
              street2: form.street2,
              city: form.city,
              state: form.state,
              zip: form.zip,
              country: form.country,
              phone: form.phone,
              email: form.email,
            },
            notes: form.orderNotes,
            date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          })
        );
      } catch { /* sessionStorage full / private mode — non-fatal */ }

      setPlacedOrderNum(`VIN-${order.id}`);
      clear();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  /* ── Empty cart state ── */
  if (items.length === 0 && !placedOrderNum) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
          <h2 className={styles.emptyTitle}>Your cart is empty</h2>
          <p className={styles.emptySubtitle}>Add some products to your cart before proceeding to checkout.</p>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/catalog')}>
            Browse Catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form className={styles.checkoutLayout} onSubmit={handlePlaceOrder}>
        {/* ── LEFT: Billing form ── */}
        <section className={styles.billingCard}>
          <h2 className={styles.sectionTitle}>Billing Detail</h2>

          {/* Saved addresses pills */}
          {savedAddresses.length > 0 && (
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Saved Addresses</label>
              <div className={styles.addressPills}>
                {savedAddresses.map((addr) => {
                  const active = selectedAddressId === addr.id;
                  return (
                    <button
                      type="button"
                      key={addr.id}
                      onClick={() => handlePickAddress(addr.id)}
                      className={`${styles.pill} ${active ? styles.pillActive : ''}`}
                    >
                      <span className={styles.pillLine1}>
                        {addr.address_line1}
                        {addr.is_default && <span className={styles.pillDefault}>Default</span>}
                      </span>
                      <span className={styles.pillLine2}>
                        {addr.city}, {addr.state} {addr.zip_code}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => handlePickAddress(null)}
                  className={`${styles.pillNew} ${selectedAddressId === null ? styles.pillActive : ''}`}
                >
                  + Use a new address
                </button>
              </div>
            </div>
          )}

          {/* First / Last name */}
          <div className={styles.row2}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>First Name<span className={styles.required}>*</span></label>
              <input className={styles.input} type="text" name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Last Name<span className={styles.required}>*</span></label>
              <input className={styles.input} type="text" name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
          </div>

          {/* Company */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Company Name (Optional)</label>
            <input className={styles.input} type="text" name="company" value={form.company} onChange={handleChange} />
          </div>

          {/* Country */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Country / Region<span className={styles.required}>*</span></label>
            <select className={styles.select} name="country" value={form.country} onChange={handleChange} required>
              <option>United States (US)</option>
              <option>Canada</option>
              <option>United Kingdom</option>
              <option>Australia</option>
            </select>
          </div>

          {/* Street address */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Street Address<span className={styles.required}>*</span></label>
            <input
              className={styles.input}
              type="text"
              name="street1"
              placeholder="Street address"
              value={form.street1}
              onChange={handleChange}
              required
            />
            <input
              className={`${styles.input} ${styles.spaceTop}`}
              type="text"
              name="street2"
              placeholder="Apartment, suite, unit, etc (Optional)"
              value={form.street2}
              onChange={handleChange}
            />
          </div>

          {/* City */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Town / City<span className={styles.required}>*</span></label>
            <input className={styles.input} type="text" name="city" value={form.city} onChange={handleChange} required />
          </div>

          {/* State + Zip */}
          <div className={styles.row2}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>State / County<span className={styles.required}>*</span></label>
              <select className={styles.select} name="state" value={form.state} onChange={handleChange} required>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Zip Code<span className={styles.required}>*</span></label>
              <input className={styles.input} type="text" name="zip" value={form.zip} onChange={handleChange} required />
            </div>
          </div>

          {/* Phone + Email */}
          <div className={styles.row2}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Phone Number<span className={styles.required}>*</span></label>
              <input className={styles.input} type="tel" name="phone" value={form.phone} onChange={handleChange} required />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Email Address<span className={styles.required}>*</span></label>
              <input className={styles.input} type="email" name="email" value={form.email} onChange={handleChange} required />
            </div>
          </div>

          {/* Create account checkbox */}
          <label className={styles.checkboxRow}>
            <input type="checkbox" name="createAccount" checked={form.createAccount} onChange={handleChange} />
            <span>Create an account?</span>
          </label>

          <h2 className={`${styles.sectionTitle} ${styles.sectionTitleExtra}`}>Additional Information</h2>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Order Notes (Optional)</label>
            <textarea
              className={styles.textarea}
              name="orderNotes"
              placeholder="Note about your order, e.g. special note for delivery"
              value={form.orderNotes}
              onChange={handleChange}
            />
          </div>
        </section>

        {/* ── RIGHT: Order summary ── */}
        <aside className={styles.summaryCard}>
          <h2 className={styles.summaryTitle}>Your Order</h2>

          <table className={styles.orderTable}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={styles.right}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((line) => (
                <tr key={line.sku_id}>
                  <td>
                    <div className={styles.itemRow}>
                      {line.image_url ? (
                        <img src={line.image_url} alt="" className={styles.itemThumb} />
                      ) : (
                        <div className={styles.itemThumb} aria-hidden />
                      )}
                      <div className={styles.itemInfo}>
                        <span className={styles.itemName}>{line.name}</span>
                        <div className={styles.qtyStepper}>
                          <button type="button" onClick={() => setQuantity(line.sku_id, line.quantity - 1)} aria-label="Decrease">−</button>
                          <input
                            type="number"
                            min="1"
                            value={line.quantity}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              if (!isNaN(v) && v >= 1) setQuantity(line.sku_id, v);
                            }}
                          />
                          <button type="button" onClick={() => setQuantity(line.sku_id, line.quantity + 1)} aria-label="Increase">+</button>
                          <button type="button" className={styles.removeBtn} onClick={() => removeItem(line.sku_id)} aria-label="Remove">×</button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={styles.right}>${(line.unit_price * line.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.summaryRows}>
            <div className={styles.summaryRow}>
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Shipping</span>
              {isFreeShipping ? (
                <span className={styles.freeShipping}>Free shipping</span>
              ) : (
                <span>${shipping.toFixed(2)}</span>
              )}
            </div>
            <div className={styles.summaryRow}>
              <span>Tax (8%)</span>
              <span>${tax.toFixed(2)}</span>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.summaryRowsTotal}>
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>

          <div className={styles.paymentSection}>
            <h3 className={styles.paymentTitle}>Payment Method</h3>
            {PAYMENT_METHODS.map((pm) => (
              <div className={styles.paymentOption} key={pm.id}>
                <label className={styles.paymentRadio}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={pm.id}
                    checked={paymentMethod === pm.id}
                    onChange={() => setPaymentMethod(pm.id)}
                  />
                  <span>{pm.label}</span>
                </label>
                {paymentMethod === pm.id && (
                  <p className={styles.paymentDesc}>{pm.desc}</p>
                )}
              </div>
            ))}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button
            type="submit"
            className={styles.placeOrderBtn}
            disabled={placing || !isFormValid}
          >
            {placing ? 'Placing Order…' : 'Place Order →'}
          </button>

          <p className={styles.privacyNote}>
            Your personal data will be used to process your order, support your experience throughout this website,
            and for other purposes described in our privacy policy.
          </p>
        </aside>
      </form>

      {/* Success overlay */}
      {placedOrderNum && (
        <div className={styles.successOverlay}>
          <div className={styles.successCard}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successHeading}>Order Placed Successfully!</h2>
            <p className={styles.successText}>
              Your order <strong>{placedOrderNum}</strong> has been placed.
            </p>
            <div className={styles.successActions}>
              <button type="button" className={styles.primaryBtn} onClick={() => navigate('/catalog')}>
                Continue Shopping →
              </button>
              <button type="button" className={styles.outlineBtn} onClick={() => navigate('/customer-orders')}>
                View Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const CustomerCartPage: React.FC = () => <CustomerCart />;
export { CustomerCart };
export default CustomerCart;
